import { IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { Plus, XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { useParams, useRouteContext } from "@tanstack/react-router";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { useGetSiteDonorsQuery } from "generated/graphql";
import { type ReactNode, useState } from "react";
import { useNavSites } from "src/components/nav-layout/site-context/use-nav-sites";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { useControlledDebounce } from "src/utils/hooks/debounce";
import { twMerge } from "tailwind-merge";

export const DonorPicker = ({
  value,
  onChange,
  newDonorOption = false,
  placeholder,
  "data-cy": dataCy,
  children,
}: {
  value: string | undefined | null;
  onChange: (val: string | undefined | null) => void;
  newDonorOption?: boolean;
  placeholder?: string;
  children: ReactNode;
  "data-cy"?: string;
}) => {
  const isDesktop = useMediaQuery("md");
  const [search, setSearch] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchTermDebouncer = useControlledDebounce((val: string) => {
    setDebouncedSearchTerm(val);
  });
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ strict: false });
  const settingsSites = useNavSites();
  const [open, setOpen] = useState(false);
  const slug = siteSlug ?? settingsSites.activeSite?.urlSlug;

  const [{ data: siteDonorsData, fetching: siteDonorsFetching }] =
    useGetSiteDonorsQuery({
      variables: {
        organisationSubdomain: subdomain,
        collectionSiteSlug: slug ?? "",
        searchTerm: debouncedSearchTerm,
        last: 30,
      },
      pause: slug == null,
    });

  const handleChange = (donor: string) => {
    if (value === donor) {
      onChange(null);
    } else {
      onChange(donor);
    }
    setOpen(false);
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    searchTermDebouncer.schedule(val);
  };

  const fetching = siteDonorsFetching;
  let donors = siteDonorsData?.org?.site?.donors?.nodes ?? [];
  if (
    value != null &&
    value !== "" &&
    donors.length > 0 &&
    !donors.some((d) => d.name.toLowerCase() === value.toLowerCase())
  ) {
    donors = [{ name: value }, ...donors];
  }

  const showNewDonorOption =
    newDonorOption &&
    search !== "" &&
    !donors.some(
      (donor) => donor.name.toLowerCase() === debouncedSearchTerm.toLowerCase(),
    );

  const commandContent = (
    <>
      <CommandEmpty>No results</CommandEmpty>
      <CommandList className="px-0 py-0">
        <div className="divide-y divide-grey-200">
          {donors.length > 0 && (
            <CommandGroup className="p-1">
              {donors.map((donor) => {
                const matches = match(donor.name, search, {
                  insideWords: true,
                });
                const parts = parse(donor.name, matches);
                const selected = value === donor.name;
                return (
                  <CommandItem
                    key={donor.name}
                    value={donor.name}
                    keywords={[donor.name]}
                    onSelect={() => {
                      handleChange(donor.name);
                      handleSearchChange("");
                    }}
                    data-cy="donor-picker-item"
                    selected={selected}
                  >
                    <div className="truncate">
                      {parts.map((part, index) => (
                        <span
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className={twMerge(
                            part.highlight && "font-bold text-grey-900",
                          )}
                        >
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {showNewDonorOption && (
            <CommandGroup className="border-grey-200 p-1 pb-0">
              <CommandItem
                value="new-donor"
                onSelect={() => {
                  setOpen(false);
                  handleChange(search);
                  handleSearchChange("");
                }}
                data-cy="donor-picker-add-new-item"
              >
                <Plus className="shrink-0 text-grey-500" />
                <div>
                  New donor/supplier
                  {search !== "" && (
                    <>
                      :{" "}
                      <span className="flex-wrap break-all font-normal text-grey-500">
                        &apos;{search}&apos;
                      </span>
                    </>
                  )}
                </div>
              </CommandItem>
            </CommandGroup>
          )}
        </div>
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          data-cy={dataCy ?? "donor-picker"}
          asChild
          onFocusCapture={stopEventPropagation}
        >
          {children}
        </PopoverTrigger>
        <PopoverContent className="z-99999 min-w-(--radix-popover-trigger-width)!">
          <Command shouldFilter={false}>
            {" "}
            <CommandInput
              placeholder={placeholder ?? "Select a donor / supplier..."}
              value={search}
              onValueChange={handleSearchChange}
              fetching={fetching}
              data-cy="donor-picker-input"
            />
            {commandContent}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        data-cy={dataCy ?? "donor-picker"}
        asChild
        onFocusCapture={stopEventPropagation}
      >
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-none p-0!">
        <Command shouldFilter={false} size="lg">
          <CommandInput
            placeholder={placeholder ?? "Select a donor / supplier..."}
            value={search}
            onValueChange={handleSearchChange}
            fetching={fetching}
            data-cy="donor-picker-input"
          >
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandInput>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
